import pt from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import { ApplicationState } from '@mews-commander/types';
import { getFullName } from '@mews-commander/utils';
import { withMewsSrcSet } from '@mews/business-components';
import { ImageResizeMode } from '@mews/enums';
import { RouterService } from '@mews/framework';
import { Avatar, getSpacingToken, Icon, Stack, Title, TitleProps } from '@optimus-web/core';
import { getImageBaseUrl } from '../../modules/app/reducers/configuration';
import { ClassificationIcons } from './ClassificationIcons';
import { AVATAR_ENTITY_SIZES, AVATAR_RATIO, AVATAR_WIDTH_SET } from './constants';

const ClickableStack = styled(Stack)`
    ${(props) => props.onClick && `cursor: pointer;`}
`;
const EntityIconComponent = styled(Icon)`
    color: inherit;
    margin-right: ${getSpacingToken('100')}px;
`;

const AvatarWithSrcSet = withMewsSrcSet(Avatar);

const factory = ({
    FontComponent = (props: Omit<TitleProps, 'level'>) => <Title {...props} level={1} />,
    AvatarComponent = AvatarWithSrcSet,
    IconComponent = EntityIconComponent,
} = {}) =>
    class EntityTitle extends Component {
        static propTypes = {
            children: pt.node,
            imageUrl: pt.string,
            imageTitle: pt.string,
            icon: pt.string,
            onClick: pt.func,
        };

        render() {
            // @ts-expect-error $TSFixMeError: Property 'imageUrl' does not exist on type 'Readonly<{}> & Readonly<{ children?: ReactNode; }>'.
            const { children, imageUrl, imageTitle, onClick, icon } = this.props;

            const avatarProps = {
                src: imageUrl,
                title: imageTitle,
                sizes: AVATAR_ENTITY_SIZES,
                ratio: AVATAR_RATIO,
                widthSet: AVATAR_WIDTH_SET,
                size: 'medium' as const,
            };

            return (
                <ClickableStack spacing="100" itemAlignment="center" onClick={onClick}>
                    {imageUrl && <AvatarComponent {...avatarProps} />}
                    {icon && <IconComponent value={icon} />}
                    <FontComponent ellipsis>{children}</FontComponent>
                </ClickableStack>
            );
        }
    };

const EntityTitle = factory();
export default EntityTitle;

interface CustomerEntityTitleComponentProps {
    classifications?: number;
    firstName?: string;
    lastName?: string;
    imageId?: string;
    imageBaseUrl: string;
}

const CustomerEntityTitleComponent = ({
    classifications,
    firstName,
    lastName,
    imageId,
    imageBaseUrl,
    ...others
}: CustomerEntityTitleComponentProps) => {
    const title = getFullName({ firstName, lastName });
    const imageUrl = imageId && RouterService.imageUrl(imageId, imageBaseUrl, { mode: ImageResizeMode.CoverExact });

    return (
        <EntityTitle {...others} imageTitle={title} imageUrl={imageUrl}>
            {title}
            <ClassificationIcons classifications={classifications} />
        </EntityTitle>
    );
};

export const CustomerEntityTitle = connect(
    createStructuredSelector<ApplicationState, Pick<CustomerEntityTitleComponentProps, 'imageBaseUrl'>>({
        imageBaseUrl: getImageBaseUrl,
    })
)(CustomerEntityTitleComponent);

const LegacyIconComponent = styled(EntityIconComponent)`
    margin-right: ${getSpacingToken('200')}px;
`;

export const LegacyEntityTitle = factory({
    IconComponent: LegacyIconComponent,
    FontComponent: (props: Omit<TitleProps, 'level'>) => <Title {...props} level={3} />,
});
